// src/features/Units/SpeechBubble.js
import React, { useState, useEffect, useRef } from 'react';

const SpeechBubble = ({ 
  hotspot, 
  onClose, 
  containerRef 
}) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const bubbleRef = useRef(null);

  // Calculate optimal bubble position
  useEffect(() => {
    if (!containerRef?.current || !bubbleRef.current || !hotspot) {
      return;
    }

    const container = containerRef.current.getBoundingClientRect();
    
    // Convert hotspot percentage to pixel position within the container
    const hotspotX = (hotspot.x / 100) * container.width;
    const hotspotY = (hotspot.y / 100) * container.height;
    
    // Calculate bubble position with smart positioning
    let bubbleX = hotspotX - 140; // Center the 280px wide bubble
    let bubbleY = hotspotY - 150; // Position above hotspot
    
    // Adjust if bubble goes off-screen horizontally
    if (bubbleX < 10) bubbleX = 10;
    if (bubbleX + 280 > container.width - 10) {
      bubbleX = container.width - 290;
    }
    
    // Adjust if bubble goes off-screen vertically
    if (bubbleY < 10) {
      bubbleY = hotspotY + 50; // Show below if no space above
    }
    
    setPosition({ x: bubbleX, y: bubbleY });
    
    // Trigger entrance animation
    setTimeout(() => setIsVisible(true), 50);
  }, [hotspot, containerRef]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose(), 200);
  };

  if (!hotspot) return null;

  return (
    <div
        ref={bubbleRef}
        className="speech-bubble no-audio-tokens"
        style={{
          position: 'absolute',
          left: `${position.x}px`,
          top: `${position.y}px`,
          zIndex: 1000,
          transform: `scale(${isVisible ? 1 : 0.3}) translateY(${isVisible ? 0 : 20}px)`,
          opacity: isVisible ? 1 : 0,
          transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
          width: '280px',
          pointerEvents: 'auto'
        }}
      >
      {/* Main Speech Bubble */}
      <div
        style={{
          background: 'rgba(255, 255, 255, 0.25)',
          backdropFilter: 'blur(25px)',
          WebkitBackdropFilter: 'blur(25px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '20px',
          padding: '24px',
          boxShadow: `
            0 20px 40px rgba(0, 0, 0, 0.15),
            0 8px 16px rgba(0, 0, 0, 0.1),
            0 2px 4px rgba(0, 0, 0, 0.05),
            inset 0 1px 0 rgba(255, 255, 255, 0.6)
          `,
          position: 'relative',
          fontFamily: '"Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
        }}
      >
        {/* Prominent Close Button */}
        <button
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            background: 'rgba(255, 255, 255, 0.15)',
            color: '#666',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            fontWeight: 'bold',
            transition: 'all 0.2s ease',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            zIndex: 10
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(255, 100, 100, 0.2)';
            e.target.style.borderColor = 'rgba(255, 100, 100, 0.4)';
            e.target.style.color = '#d63384';
            e.target.style.transform = 'scale(1.1)';
            e.target.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.15)';
            e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
            e.target.style.color = '#666';
            e.target.style.transform = 'scale(1)';
            e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
          }}
        >
          ×
        </button>

        {/* Content - Just the text, no audio controls */}
        <div style={{ paddingRight: '24px' }}>
          <p
            style={{
              margin: '0',
              fontSize: '16px',
              lineHeight: '1.6',
              color: '#1a1a1a',
              textShadow: '0 1px 3px rgba(255, 255, 255, 0.8)',
              fontWeight: '500',
              letterSpacing: '0.02em'
            }}
          >
            {hotspot.text}
          </p>
        </div>

        {/* Speech Bubble Tail - Pointing to hotspot */}
        <div
          style={{
            position: 'absolute',
            bottom: '-12px',
            left: '50%',
            width: '24px',
            height: '24px',
            background: 'rgba(255, 255, 255, 0.25)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderTop: 'none',
            borderLeft: 'none',
            transform: 'translateX(-50%) rotate(45deg)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            boxShadow: '2px 2px 8px rgba(0, 0, 0, 0.1)'
          }}
        />

        {/* Additional floating effect with subtle gradient overlay */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: '20px',
            background: `
              linear-gradient(135deg, 
                rgba(255, 255, 255, 0.15) 0%, 
                rgba(255, 255, 255, 0.05) 50%, 
                rgba(0, 0, 0, 0.01) 100%
              )
            `,
            pointerEvents: 'none',
            zIndex: 1
          }}
        />
      </div>
    </div>
  );
};

export default SpeechBubble;